import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref } from 'vue';
import { MonetizationType, ObjectType, PresentationType, OfferType } from '@/@types/graphql-types';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { useAppStore, useLanguageStore } from '@/helpers/composables/useStores';
import {
	BuyboxParam,
	DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY,
	MONETIZATION_TYPE_TRANSLATION_KEY,
	OfferLocation,
} from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { shouldClickMask, clickMaskUrl } from '@/components/click-masking/click-masking-config';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';

import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ModalHelper } from '@/helpers/modal-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { diffInDays, formatDateTime } from '@/helpers/date-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { SnowplowPackageContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { SponsoredClickoutsHelper } from '@/features/sponsored-recommendations/helpers/sponsored-clickouts-helper';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';
import { useProviderPromotion } from '@/helpers/provider-promotion';

import {
	useInternationalOffers,
	useInternationalOffersStatus,
} from '@/features/international-offers/composables/useInternationalOffers';

import { faClock } from '@fortawesome/free-solid-svg-icons';
import DisclaimerButton from '@/features/buybox/components/DisclaimerButton.vue';
import { getDisclaimerText } from '@/features/buybox/helpers/list-buybox-config';

// JW_APP_DOWNLOAD_EXP_1
import { AppDownloadModal } from '@/lazy-load-components';
import { isMobile } from '@/helpers/ui-helper';
import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { useActivateExperiment } from '@/libraries/experiment';
// JW_APP_DOWNLOAD_EXP_1

import { isBitMovinCountry } from '@/helpers/trailer-helper';
import { BuyboxRegularOffer } from '@/features/buybox/types/types';

import ListOfferCTA from '@/features/buybox/components/list/ListOfferCTA.vue';
import ListOfferPresentation from '@/features/buybox/components/list/ListOfferPresentation.vue';
import { useDoubleStackExp } from '@/components/experiments/DoubleStackExp';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { isJWTVPricingExpShouldBeActive, useJWTVPricingExp } from '@/components/experiments/JWTVPricingExp';


export default /*#__PURE__*/_defineComponent({
  __name: 'ListOffer',
  props: {
    offer: null,
    title: null,
    isPromotedBuyBox: { type: Boolean },
    isBundle: { type: Boolean },
    isEpisode: { type: Boolean },
    seasonNumber: null,
    episodeNumber: null,
    additionalContexts: null
  },
  emits: ["clickOut"],
  setup(__props: any) {

const props = __props as {
	offer: BuyboxRegularOffer;
	title: TitleDetail;
	isPromotedBuyBox?: boolean;
	isBundle?: boolean;
	isEpisode?: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	additionalContexts: SnowplowContext[];
};

const BuyBoxInfoModal = () => import('@/features/buybox/components/list/BuyBoxInfoModal.vue');
const BundleIcons = () => import('@/features/buybox/components/list/BundleIcons.vue');




const { country, language, webLocale } = useLanguageStore();
const { t, tc } = useI18n();
const { xServeTrack } = useAppStore();

const buyboxOffer = computed(() => props.offer);

const { setDoubleStackTags } = useDoubleStackExp();

const isDvdOrBluRay = computed(() =>
	[PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
		buyboxOffer.value?.presentationType
	)
);

const isBundleWithIconWide = computed(() => props.isBundle && buyboxOffer.value.bundleShortName === 'pap');

const iconUrl = computed(() => {
	if (isBundleWithIconWide.value) return 'https://www.justwatch.com/appassets/img/bundles/applepeacock.{format}';

	return buyboxOffer.value.package.iconWide || buyboxOffer.value.package.icon;
});

const displayBuyBoxInfoModal = ref<boolean>(false);
const offerContainer = ref<HTMLElement | null>(null);

const isModalProvider = computed(() =>
	['plexplayer', 'justwatchtv', 'ottstudioplayer'].includes(buyboxOffer.value.package.technicalName)
);

const _clickoutType = computed(() => {
	if (isModalProvider.value) return 'video_player';
	return props.isPromotedBuyBox ? 'promoted' : 'regular';
});

const { clickoutType, placement } = getClickoutTrackingPlacement(_clickoutType.value);

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

const offerLocationInjection = inject<OfferLocation>('offerLocation');
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const noOffer = inject<boolean | undefined>('noOffer', undefined);

const { isPromotionOffer, providerPromotion: providerPromo } = useProviderPromotion();

const isProviderPromo = computed(() => !buyboxOffer.value.bundleId && isPromotionOffer(buyboxOffer.value));

const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);

const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage({ entityId: jwRawEntityId });

const formattedDate = computed(() => {
	const ESTTimeZone = 'America/New_York';
	const PSTTimeZone = 'America/Los_Angeles';

	if (!buyboxOffer.value?.availableFromTime) {
		return null;
	}

	return {
		Day: formatDateTime(new Date(buyboxOffer.value.availableFromTime), language.value, country.value, {
			day: 'numeric',
			month: 'short',
			timeZone: ESTTimeZone,
		}),
		PST:
			formatDateTime(new Date(buyboxOffer.value.availableFromTime), language.value, country.value, {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: PSTTimeZone,
			}) + ' PST',
		EST:
			formatDateTime(new Date(buyboxOffer.value.availableFromTime), language.value, country.value, {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: ESTTimeZone,
			}) + ' EST',
	};
});

const shouldMaskClicks = computed(() => shouldClickMask(buyboxOffer.value?.package.packageId, country.value));

const rowNumber = computed(() => {
	if (offerContainer.value == null) return undefined;

	const parentBuyBox = offerContainer.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(offerContainer.value));

	return index === -1 ? undefined : index;
});

// JW_APP_DOWNLOAD_EXP_1
const shouldActivateJwAppDlExperiment = computed(() => {
	return isMobile() && props.offer.package.packageId === 2285;
});

const { activeVariantWithControlGroup, trackEvent } = useActivateExperiment(
	'JW_APP_DOWNLOAD_EXP_1',
	shouldActivateJwAppDlExperiment
);
// JW_APP_DOWNLOAD_EXP_1

// EXP_JWTV_PRICING
const jwtvPricingExpCondition = computed(() => isJWTVPricingExpShouldBeActive(buyboxOffer.value));
const { activeVariant } = useActivateExperiment('EXP_JWTV_PRICING', jwtvPricingExpCondition);
const { activePromo } = useJWTVPricingExp(); // just to make sure the experiment is activated
const jwtvPriceOverride = computed(() => {
	const retailPrice = buyboxOffer.value.retailPriceValue;
	if (activePromo.value && retailPrice && activeVariant.value) {
		return activePromo.value?.calculatePrice?.(retailPrice) ?? retailPrice;
	}
	return null;
});
// EXP_JWTV_PRICING

const promoRibbonText = computed(() => providerPromo.value?.wideRibbon ?? providerPromo.value?.ribbon);

const disclaimerText = computed(() => {
	if (isProviderPromo.value && providerPromo.value.offerText && providerPromo.value.offerDisclaimerText)
		return providerPromo.value.offerDisclaimerText;

	if (props.offer.package.shortName === 'adb' && isDvdOrBluRay.value) {
		return tc('WEBAPP_AMAZON_DVD_BLURAY_PRICE_DISCLAIMER');
	}

	return getDisclaimerText(props.offer.package.shortName, country.value);
});

const showDisclaimerButton = computed(() => !!disclaimerText.value);

const { rowCountry } = useInternationalOffers();
const { isActive: isInternationalOffersActive } = useInternationalOffersStatus({ title: () => props.title });

function clickoutBase(url: ClickoutUrl) {
	return url
		.addContexts(titleContext.value)
		.setForTracking('offerLocation', offerLocationInjection)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.updateContext(
			'ClickoutContextGraphql',
			'placement',
			isInternationalOffersActive.value ? `international_offers${props.isEpisode ? '_episode' : ''}` : placement
		)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.setTitleSourceId(titleSourceId)
		.set('uct_country', country.value)
		.set('uct_buybox', props.isPromotedBuyBox ? BuyboxParam.Promoted : BuyboxParam.Normal)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.setCanary(xServeTrack.value);
}

const offerUrl = computed(() => {
	let clickoutUrl = new ClickoutUrl(buyboxOffer.value);

	if (isProviderPromo.value && providerPromo.value?.url) {
		const offer = { ...buyboxOffer.value, standardWebURL: providerPromo.value.url };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (shouldMaskClicks.value) {
		const maskedUrl = clickMaskUrl(buyboxOffer.value.standardWebURL!);
		const offer = { ...buyboxOffer.value, standardWebURL: maskedUrl };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (isArbitrating.value && ['amp', 'atp'].includes(buyboxOffer.value.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({ offer: buyboxOffer, title: () => props.title, placement: 'ro' });

		if (arbitrageUrl) {
			return arbitrageUrl.apply(clickoutBase);
		}
	}

	const url = clickoutUrl.apply(clickoutBase);

	if (rowCountry.value) {
		url.set('uct_country', rowCountry.value);
	}

	if (props.isBundle) {
		const packageContext = new SnowplowPackageContext(
			buyboxOffer.value.package.packageId,
			buyboxOffer.value.package.technicalName,
			'bundle',
			buyboxOffer.value.bundleId,
			buyboxOffer.value.bundleTechnicalName
		);
		clickoutUrl.addContexts(packageContext);

		// Bypass UCT for US users on bundle amazonstarzamazonhbomax
		if (buyboxOffer.value.bundleTechnicalName === 'amazonstarzamazonhbomax') {
			url.set('uct_ct', 'bypass');
		}
	}

	url.apply(url => setDoubleStackTags(url, 'ro', buyboxOffer.value.package.shortName));

	// Amazon streaming bounty redirects
	if ([9, 119].includes(buyboxOffer.value.package.packageId)) {
		const redirect = url.get('r');
		return url.set('r', redirect!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return url;
});

const isLeavingIconShown = computed(() => {
	return (
		!!buyboxOffer.value.availableTo &&
		diffInDays(new Date(buyboxOffer.value.availableTo)) < 30 &&
		diffInDays(new Date(buyboxOffer.value.availableTo)) >= 0
	);
});

const leavingLabel = computed(() => {
	if (!isLeavingIconShown.value) return '';
	const diffDays = diffInDays(new Date(buyboxOffer.value.availableTo));
	if (diffDays === 0) return tc('WEBAPP_LEAVING_TODAY');
	if (diffDays === 1) return tc('WEBAPP_LEAVING_TOMORROW');
	return t('WEBAPP_LEAVING_X_DAYS', { days: diffDays });
});

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

const selectedPackagePlan = computed(() => {
	// we have plan offers in 2 separate places because of international offers.
	// for gql it's not easily doable to include international offer's provider plan offers in the package.
	// that's why we take both places here into account.
	const planOffers = buyboxOffer.value?.plans.length
		? buyboxOffer.value?.plans
		: buyboxOffer.value?.package.planOffers;
	if (planOffers.length) {
		const plan = planOffers[0];
		// if a plan has a children that means it's a free trial plan and that always has priority.
		if (plan.children.length > 0) {
			return {
				...plan.children[0],
				retailPrice: plan.retailPrice,
				retailPriceConverted: plan.retailPriceConverted,
			};
		}
		return plan;
	}
	return null;
});

const freeTrialText = computed(() => {
	if (props.isBundle) {
		return null;
	}
	// Free trial data from API
	if (selectedPackagePlan.value?.isTrial) {
		if (selectedPackagePlan.value.durationDays === 1) return t('WEBAPP_1_DAY_FREE');
		return t('WEBAPP_X_DAYS_FREE', {
			days: selectedPackagePlan.value.durationDays,
		});
	}
	return null;
});

const hasFreeTrial = computed(
	() => !!freeTrialText.value && buyboxOffer.value?.monetizationType === MonetizationType.Flatrate
);

// currently used by Sky DE only, this replaces the retail price with 'Sky Angebote' text
const hasOfferDetails = computed(() => buyboxOffer.value.package.shortName === 'skg' && country.value === 'DE');
const offerDetails = computed(() => {
	const provider = buyboxOffer.value.package.shortName === 'skg' ? 'Sky' : buyboxOffer.value.package.technicalName;

	return t('WEBAPP_PROVIDER_OFFER', { provider });
});

const retailPrice = computed(() => {
	if (props.isBundle) {
		return null;
	} else if (buyboxOffer.value?.retailPrice) {
		if (
			buyboxOffer.value.country !== country.value &&
			buyboxOffer.value.retailPriceConverted &&
			buyboxOffer.value.retailPrice != buyboxOffer.value.retailPriceConverted
		) {
			return `${buyboxOffer.value.retailPrice} (${buyboxOffer.value.retailPriceConverted})`;
		}
		return buyboxOffer.value.retailPrice;
	} else if (selectedPackagePlan.value && selectedPackagePlan.value.retailPrice) {
		// Retail price from API

		let retailPrice = selectedPackagePlan.value.retailPrice;
		if (
			buyboxOffer.value.country !== country.value &&
			selectedPackagePlan.value?.retailPriceConverted &&
			selectedPackagePlan.value.retailPrice != selectedPackagePlan.value.retailPriceConverted
		) {
			retailPrice = `${selectedPackagePlan.value.retailPrice} (${selectedPackagePlan.value.retailPriceConverted})`;
		}

		return t(
			buyboxOffer.value.monetizationType === MonetizationType.Flatrate && hasFreeTrial.value
				? 'WEBAPP_THEN_X_PER_MONTH'
				: 'WEBAPP_X_PER_MONTH',
			{ price: retailPrice }
		);
	}
	return null;
});

const isShowPageTVOD = computed(
	() =>
		[MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType) &&
		props.title.objectType === ObjectType.Show &&
		!props.isEpisode
);

const minEpisodeTVODPriceOnShows = computed(() => {
	if (
		!isShowPageTVOD.value ||
		props.title.__typename !== 'Show' ||
		!buyboxOffer.value.minRetailPrice ||
		isDvdOrBluRay.value
	)
		return null;

	return buyboxOffer.value.minRetailPrice;
});

const minSeasonTVODPriceOnShows = computed(() => {
	const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
	if (
		props.title.__typename !== 'Show' ||
		!seasonIdsOnOffer.length ||
		![MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType)
	) {
		return null;
	}
	// all seasonIds that are on the Show offer
	const filteredSeasons = props.title.seasons.filter(season => seasonIdsOnOffer.includes(season.id));
	if (!filteredSeasons.length) return null;

	// find the lowest price of all seasons
	const formattedMinPrice = filteredSeasons
		.map(season =>
			season.offers.find(
				offer =>
					offer.monetizationType === buyboxOffer.value.monetizationType &&
					buyboxOffer.value.package.shortName === offer.package.shortName &&
					buyboxOffer.value.presentationType === offer.presentationType
			)
		)
		.filter(offer => offer?.retailPrice != null && offer?.retailPriceValue != null && offer?.retailPriceValue !== 0)
		.sort((a, b) => a!.retailPriceValue! - b!.retailPriceValue!)[0]?.retailPrice;

	return formattedMinPrice;
});

const minSeasonTVODPriceOnShowsText = computed(() => {
	const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
	if (
		props.title.__typename !== 'Show' ||
		!seasonIdsOnOffer.length ||
		![MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType)
	) {
		return null;
	}

	if (isDvdOrBluRay.value) {
		const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
		const filteredSeasons = props.title.seasons.filter(
			season =>
				seasonIdsOnOffer.includes(season.id) &&
				season.offers.find(
					offer =>
						offer.monetizationType === buyboxOffer.value.monetizationType &&
						buyboxOffer.value.package.shortName === offer.package.shortName &&
						buyboxOffer.value.presentationType === offer.presentationType
				)
		);
		if (buyboxOffer.value.type === OfferType.Standard) {
			if (buyboxOffer.value.country !== country.value && buyboxOffer.value.retailPriceConverted) {
				return `${buyboxOffer.value.retailPrice} (${buyboxOffer.value.retailPriceConverted})`;
			}
			return buyboxOffer.value.retailPrice;
		}
		if (filteredSeasons.length <= 1) {
			return minSeasonTVODPriceOnShows.value;
		}
		return t('WEBAPP_MIN_PRICE_PER_SEASON', {
			price: minSeasonTVODPriceOnShows.value,
		});
	} else {
		return t('WEBAPP_MIN_PRICE_PER_SEASON', {
			price: minSeasonTVODPriceOnShows.value,
		});
	}
});

const hasMinPriceTVOD = computed(() => !!minEpisodeTVODPriceOnShows.value || !!minSeasonTVODPriceOnShows.value);

const showRetailPrice = computed(
	() => !!retailPrice.value && buyboxOffer.value?.monetizationType !== MonetizationType.Free
);

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(buyboxOffer.value?.package.packageId, webLocale.value)
		? 'nofollow sponsored noopener'
		: 'nofollow noopener'
);

const playerModalByProvider: Record<string, { cmp: () => Promise<any> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	},
	justwatchtv: {
		cmp: () => import('@/ui-components/video-player-modals/JwTVModal.vue'),
	},
	ottstudioplayer: {
		cmp: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
	},
};

const { openRentalModal } = useRentalModal();
async function openModal() {
	if (!isModalProvider.value) return;

	if (isJWTVRentalOffer(buyboxOffer.value)) {
		// we trigger clickout first to get the final link context id as it's used to link events especially after page redirects.
		trackClickout(offerUrl.value);
		openRentalModal({
			offer: buyboxOffer.value,
			title: props.title,
			clickoutUrl: offerUrl.value,
			forceSafeOpen: isInternationalOffersActive.value,
			// EXP_JWTV_PRICING
			expPromoCode: activeVariant.value ? activePromo.value : null,
		});
		return;
	}

	// JW_APP_DOWNLOAD_EXP_1
	if (
		props.offer.package.packageId === 2285 &&
		['variant_1', 'variant_2'].includes(activeVariantWithControlGroup.value ?? '')
	) {
		trackEvent({
			action: 'click',
			label: 'jwtv_offer',
			property: 'jwtv_offer',
			contexts: [SnowplowClickoutContextGraphql.fromProviderOffer(props.offer)],
		});
		const clip = {
			provider: 'JUSTWATCH_TV',
			externalId: props.offer.id,
			sourceUrl: isBitMovinCountry(country.value)
				? props.offer.streamUrlExternalPlayer ?? props.offer.streamUrl
				: props.offer.streamUrl,
		};

		ModalHelper.openModal(
			AppDownloadModal,
			{
				clips: [clip],
				title: props.title,
				source: 'title-detail',
				additionalContexts: [...props.additionalContexts],
				variant: activeVariantWithControlGroup.value,
			},
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);

		trackEvent({
			action: 'impression',
			label: 'watch_now',
			property: 'watch_now',
			contexts: [SnowplowClickoutContextGraphql.fromProviderOffer(props.offer)],
		});

		return;
	} else if (props.offer.package.packageId === 2285 && activeVariantWithControlGroup.value === 'control') {
		trackEvent({
			action: 'click',
			label: 'jwtv_offer',
			property: 'jwtv_offer',
			contexts: [SnowplowClickoutContextGraphql.fromProviderOffer(props.offer)],
		});
	}
	// JW_APP_DOWNLOAD_EXP_1
	trackClickout(offerUrl.value);

	const modalData = playerModalByProvider[buyboxOffer.value.package.technicalName];
	ModalHelper.openModal(
		modalData.cmp,
		{
			offer: buyboxOffer.value,
			title: props.title,
			additionalContexts: getClickoutUrlContextsArray(offerUrl.value),
		},
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	handleClickOut();
}

function toggleBuyBoxInfoModal(display: boolean) {
	displayBuyBoxInfoModal.value = display;

	if (displayBuyBoxInfoModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'more_info_clicked', label: 'buybox' },
			props.additionalContexts
		);
	}
}

function handleClickOut() {
	trackTagEvent(() => buyboxOffer.value);
}

function onVisibilityChanged(isVisible: boolean) {
	onIntersection(offerUrl.value, isVisible);

	// JW_APP_DOWNLOAD_EXP_1
	if (props.offer.package.packageId === 2285 && isVisible && activeVariantWithControlGroup.value) {
		trackEvent({
			action: 'impression',
			label: 'jwtv_offer',
			property: 'jwtv_offer',
			contexts: [SnowplowClickoutContextGraphql.fromProviderOffer(props.offer)],
		});
	}
	// JW_APP_DOWNLOAD_EXP_1
}

function offerLabel(monetizationType: MonetizationType, presentationType: PresentationType) {
	if (isProviderPromo.value && providerPromo.value.offerText) return providerPromo.value.offerText;
	if (props.isBundle) {
		return tc('WEBAPP_BUNDLE');
	}

	if (monetizationType === MonetizationType.Fast && formattedDate.value?.['Day']) {
		return formattedDate.value?.['Day'];
	}

	if (
		presentationType === PresentationType.Dvd ||
		presentationType === PresentationType.Bluray ||
		presentationType === PresentationType.Bluray_4K
	) {
		if (monetizationType in DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY[presentationType]) {
			return tc(DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY[presentationType][monetizationType] as string);
		}
	}

	return tc(MONETIZATION_TYPE_TRANSLATION_KEY[monetizationType]);
}

return { __sfc: true,BuyBoxInfoModal, BundleIcons, props, country, language, webLocale, t, tc, xServeTrack, buyboxOffer, setDoubleStackTags, isDvdOrBluRay, isBundleWithIconWide, iconUrl, displayBuyBoxInfoModal, offerContainer, isModalProvider, _clickoutType, clickoutType, placement, onIntersection, offerLocationInjection, titleContext, filterOption, titleSourceId, episodeEntityId, noOffer, isPromotionOffer, providerPromo, isProviderPromo, jwRawEntityId, isArbitrating, withArbitrageTracking, trackTagEvent, formattedDate, shouldMaskClicks, rowNumber, shouldActivateJwAppDlExperiment, activeVariantWithControlGroup, trackEvent, jwtvPricingExpCondition, activeVariant, activePromo, jwtvPriceOverride, promoRibbonText, disclaimerText, showDisclaimerButton, rowCountry, isInternationalOffersActive, clickoutBase, offerUrl, isLeavingIconShown, leavingLabel, observeVisibilityOptions, selectedPackagePlan, freeTrialText, hasFreeTrial, hasOfferDetails, offerDetails, retailPrice, isShowPageTVOD, minEpisodeTVODPriceOnShows, minSeasonTVODPriceOnShows, minSeasonTVODPriceOnShowsText, hasMinPriceTVOD, showRetailPrice, isSponsoredOffer, playerModalByProvider, openRentalModal, openModal, toggleBuyBoxInfoModal, handleClickOut, onVisibilityChanged, offerLabel, ClickoutAnchor, ProviderIcon, faClock, DisclaimerButton, ListOfferCTA, ListOfferPresentation }
}

})